import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  useMediaQuery,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import api, { checkActiveWebhookByTopic } from '../../services/api';
import ServerDataTable from '../ServerDataTable/ServerDataTable';
import JsonDataDisplay from '../common/JsonDataDisplay';
import { formatDate } from '../../utils/formatDate';
import { useConfigurationStatus } from '../../hooks/useConfigurationStatus';
import StatusBadge from '../StatusBadge';

export default function SyncedOrdersTab() {
  // URL synchronization constants
  const DEFAULT_PAGE_SIZE = 10;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();

  // State for search and pagination
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: '',
    direction: ''
  });
  const [viewData, setViewData] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDialogMaximized, setIsDialogMaximized] = useState(true);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);

  // Filter states - separate state for form inputs and applied filters
  const [filterForm, setFilterForm] = useState({
    is_test_order: '',
    order_created_at_start: '',
    order_created_at_end: ''
  });

  const [appliedFilters, setAppliedFilters] = useState({
    is_test_order: '',
    order_created_at_start: '',
    order_created_at_end: ''
  });

  // Initialize filters and pagination from URL on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Parse filter parameters - use the correct field names
    const filtersFromURL = {
      is_test_order: params.get('is_test_order') || '',
      order_created_at_start: params.get('order_created_at_start') || '',
      order_created_at_end: params.get('order_created_at_end') || ''
    };

    // Parse pagination parameters
    const pageParam = params.get('page');
    const pageSizeParam = params.get('pageSize');
    const searchParam = params.get('search');
    // Update page index from URL
    if (pageParam !== null) {
      // Convert 1-indexed URL to 0-indexed internal state
      const newPageIndex = parseInt(pageParam, 10) - 1;
      setPageIndex(newPageIndex);
    } else {
      // If no page parameter, reset to first page
      setPageIndex(0);
    }

    if (pageSizeParam !== null) {
      setPageSize(parseInt(pageSizeParam, 10));
    }

    if (searchParam !== null) {
      setSearchTerm(searchParam);
    }

    // Set applied filters from URL
    setAppliedFilters(filtersFromURL);
    setFilterForm(filtersFromURL);
  }, [location.search]);

  // Additional effect to ensure URL state is properly reflected in component state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = params.get('page');

    if (pageParam !== null) {
      const urlPageIndex = parseInt(pageParam, 10) - 1;
      // Only update if the current pageIndex doesn't match the URL
      if (pageIndex !== urlPageIndex) {
        setPageIndex(urlPageIndex);
      }
    }
  }, [location.search, pageIndex]);

  // Synchronize filterForm with appliedFilters when filter dialog opens
  useEffect(() => {
    if (isFilterDialogOpen) {
      setFilterForm(appliedFilters);
    }
  }, [isFilterDialogOpen, appliedFilters]);

  // Update URL with filters and pagination
  const updateURLWithFilters = (filters, currentPage, currentPageSize, currentSearchTerm) => {
    const params = new URLSearchParams();

    // Add filter parameters to URL
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '') {
        params.set(key, value);
      }
    });

    // Add pagination parameters to URL
    if (currentPage >= 0) {
      // Convert 0-indexed internal state to 1-indexed URL
      params.set('page', (currentPage + 1).toString());
    }

    params.set('pageSize', currentPageSize.toString() || DEFAULT_PAGE_SIZE.toString());
    params.set('search', currentSearchTerm || '');
    // Update URL without reloading the page
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm?.trim());
      setPageIndex(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch order records with pagination and filters
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['orderRecords', pageIndex, pageSize, debouncedSearchTerm, appliedFilters],
    queryFn: async () => {
      try {
        const params = {
          offset: pageIndex * pageSize,
          limit: pageSize,
          search: debouncedSearchTerm || undefined,
          is_test_order: appliedFilters.is_test_order || undefined,
          order_created_at_start: appliedFilters.order_created_at_start || undefined,
          order_created_at_end: appliedFilters.order_created_at_end || undefined
        };

        const response = await api.getSyncedOrders(params);

        // Transform the response data
        const transformedData = {
          items: response.data.data.map((item) => ({
            id: item.id,
            orderNumber: item.order_number,
            orderCreatedAt: item.order_created_at,
            isTestOrder: item.is_test_order,
            orderHeadData: item.order_head_data,
            orderLinesData: item.order_lines_data,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          })),
          pagination: {
            total: response.data.totalItems || 0,
            limit: response.data.limit || pageSize,
            offset: response.data.offset || 0,
            hasMore: response.data.hasMore || false
          }
        };

        return transformedData;
      } catch (error) {
        console.error('Error fetching order records:', error);
        throw error;
      }
    }
  });

  // Get active Shopify config
  const { shopifyConfig } = useConfigurationStatus();
  const shopifyConfigId = shopifyConfig?.id;

  // Check for active orders/create webhook
  const {
    data: webhookCheck,
    isLoading: isWebhookCheckLoading
  } = useQuery({
    queryKey: ['activeOrdersCreateWebhook', shopifyConfigId],
    queryFn: async () => {
      if (!shopifyConfigId) return null;
      const response = await checkActiveWebhookByTopic(shopifyConfigId, encodeURIComponent('orders/create'));
      return response.data;
    },
    enabled: !!shopifyConfigId,
    refetchOnWindowFocus: true,
    retry: false,
    staleTime: 0
  });

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const columns = [
    {
      field: 'orderNumber',
      headerName: 'Order Number',
      width: 250,
      sortable: true
    },
    {
      field: 'isTestOrder',
      headerName: 'Test Order',
      width: 250,
      sortable: true,
      renderCell: ({ value }) => {
        return (
          <StatusBadge customLabel={value ? 'Yes' : 'No'} customColor={value ? 'error' : 'success'} customize={true} />
        );
      }
    },
    {
      field: 'orderCreatedAt',
      headerName: 'Order Created At',
      width: 250,
      sortable: true,
      renderCell: ({ value }) => formatDate(value)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title='View Details'>
          <IconButton onClick={() => handleOpenViewDialog(row)}>
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  const handleRefresh = () => {
    refetch();
  };

  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
    updateURLWithFilters(appliedFilters, newPage, pageSize, searchTerm);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageIndex(0);
    updateURLWithFilters(appliedFilters, 0, newPageSize, searchTerm);
  };

  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setPageIndex(0);
    updateURLWithFilters(appliedFilters, 0, pageSize, newSearchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPageIndex(0);
    updateURLWithFilters(appliedFilters, 0, pageSize, '');
  };

  const handleOpenViewDialog = (data) => {
    setViewData(data);
    setIsViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewData(null);
    setIsViewDialogOpen(false);
    setIsDialogMaximized(true);
  };

  const toggleDialogSize = () => {
    setIsDialogMaximized(!isDialogMaximized);
  };

  // Handle filter form changes (not applied immediately)
  const handleFilterFormChange = (event) => {
    const { name, value } = event.target;
    setFilterForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filterForm);
    setIsFilterDialogOpen(false);
    setPageIndex(0);
    updateURLWithFilters(filterForm, 0, pageSize, searchTerm);
  };

  // Open filter dialog and initialize form with current applied filters
  const handleOpenFilterDialog = () => {
    setFilterForm(appliedFilters);
    setIsFilterDialogOpen(true);
  };

  // Check if any applied filters have values (for Clear Filters button)
  const hasAppliedFilters = () => {
    return Object.values(appliedFilters).some(value => value !== '');
  };

  // Check if filter form has changed from applied filters (for Apply Filters button)
  const hasFilterFormChanges = () => {
    return Object.keys(filterForm).some(key => filterForm[key] !== appliedFilters[key]);
  };

  // Update clear filters to close modal
  const handleClearFilters = () => {
    const emptyFilters = {
      is_test_order: '',
      order_created_at_start: '',
      order_created_at_end: ''
    };
    setFilterForm(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPageIndex(0);
    setIsFilterDialogOpen(false);
    updateURLWithFilters(emptyFilters, 0, pageSize, searchTerm);
  };

  return (
    <Box>
      {/* Alert for missing/inactive orders/create webhook */}
      {shopifyConfigId && webhookCheck && !webhookCheck.found && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          <AlertTitle>Shopify Webhook Required</AlertTitle>
          The <b>orders/create</b> webhook is not registered or is inactive in your Shopify configuration. Order sync from Shopify to ERP will not work until this webhook is active.
        </Alert>
      )}
      <ServerDataTable
        columns={columns}
        currentPage={pageIndex}
        data={data?.items || []}
        totalRows={data?.pagination?.total || 0}
        sortConfig={sortConfig}
        onSort={handleSort}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[10, 25, 50, 100]}
        noDataMessage='No order records found'
        loading={isLoading || isFetching || isWebhookCheckLoading}
        isError={error}
        error={error}
        totalPages={Math.ceil(data?.pagination?.total / pageSize)}
        enableSearch={true}
        searchTerm={searchTerm}
        setSearchTerm={handleSearchChange}
        onClearSearch={handleClearSearch}
        searchPlaceholder='Search by Order Number'
        searchSize='small'
        buttons={
          <Box display='flex' gap={2} sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}>
            <Button
              variant='contained'
              startIcon={<FilterListIcon />}
              onClick={handleOpenFilterDialog}
              sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}
              disabled={isFetching}
            >
              Filters
              {!isFetching && Object.values(appliedFilters).some((value) => value !== '') && (
                <Chip
                  size='small'
                  label={isFetching ? '' : Object.values(appliedFilters).filter((value) => value !== '').length}
                  color='secondary'
                  sx={{ ml: 1 }}
                  disabled={isFetching}
                />
              )}
            </Button>
            <Button sx={{ width: { xs: '100% !important', sm: 'auto !important' } }} variant='contained' color='primary' startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={isFetching}>
              Refresh
            </Button>
          </Box>
        }
      />
      {/* Filter Dialog */}
      <Dialog
        open={isFilterDialogOpen}
        onClose={() => setIsFilterDialogOpen(false)}
        maxWidth='md'
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
          Filter Orders
          <IconButton onClick={() => setIsFilterDialogOpen(false)} size='small' sx={{ ml: 2 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>Test Order</InputLabel>
              <Select
                name='is_test_order'
                value={filterForm.is_test_order}
                onChange={handleFilterFormChange}
                label='Test Order'
              >
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='1'>Yes</MenuItem>
                <MenuItem value='0'>No</MenuItem>
              </Select>
            </FormControl>
            <Divider>Order Created At Range</Divider>
            <TextField
              name='order_created_at_start'
              label='From Date'
              type='date'
              value={filterForm.order_created_at_start}
              onChange={handleFilterFormChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              name='order_created_at_end'
              label='To Date'
              type='date'
              value={filterForm.order_created_at_end}
              onChange={handleFilterFormChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClearFilters}
            color='secondary'
            variant='contained'
            disabled={!hasAppliedFilters()}
          >
            Clear Filters
          </Button>
          <Button
            onClick={handleApplyFilters}
            color='primary'
            variant='contained'
            disabled={!hasFilterFormChanges()}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
      {/* View Dialog */}
      <Dialog
        open={isViewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth='lg'
        fullWidth={true}
        fullScreen={isDialogMaximized}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 1
          }}
        >
          Order Details
          <Box>
            <Tooltip
              title={isDialogMaximized ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              <IconButton onClick={toggleDialogSize} sx={{ mr: 0.5 }}>
                {isDialogMaximized ? (
                  <FullscreenExitIcon />
                ) : (
                  <FullscreenIcon />
                )}
              </IconButton>
            </Tooltip>
            <IconButton onClick={handleCloseViewDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {viewData && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  Order Number
                </Typography>
                <Typography variant='body1' gutterBottom color='text.secondary' component='div'>
                  {viewData.orderNumber}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div' sx={{ wordBreak: 'break-all !important' }}>
                  Test Order
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant='body1' gutterBottom sx={{ mr: 1 }} color='text.secondary' component='div'>
                    {viewData.isTestOrder ? <StatusBadge customLabel='Yes' customColor='error' customize={true} /> : <StatusBadge customLabel='No' customColor='success' customize={true} />}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  Order Created At
                </Typography>
                <Typography variant='body1' gutterBottom color='text.secondary' component='div'>
                  {formatDate(viewData.orderCreatedAt)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  Synced At
                </Typography>
                <Typography variant='body1' gutterBottom color='text.secondary' component='div'>
                  {formatDate(viewData.createdAt)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <JsonDataDisplay data={viewData.orderHeadData} title='Order Head Data' />
              </Grid>
              <Grid item xs={12}>
                <JsonDataDisplay data={viewData.orderLinesData} title='Order Lines Data' />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseViewDialog} color='secondary' variant='contained'>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
